import React, {useMemo, useRef} from 'react';
import {classes, style} from './doppe-action-type-links-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionTypeLinksList} from '../doppe-action-type-links-list';
import {
    DoppeActionType,
    useIsWidgetRenderedInPage,
    useWidgetRenderingContext
} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {asArray, evaluateFunction, iterableMapToArray, ObservableValue} from '@wix/devzai-utils-common';
import {
    DoppeActionTypeWidgetItemList,
    onListItemClick
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-list-view-item';
import {
    DoppeActionListItemClickBehaviour,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueToggleHidden
} from '../../../client-server-common';
import {useBymoPageContextProps} from '../../../doppe-sdk';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    doppeHideableValueGetValue,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {
    DoppeActionTypeLinkWidgetInner
} from '../../doppe-action-type-link-view/doppe-action-type-link-view-widget/doppe-action-type-link-view-widget';
import {DoppeViewerActionContext} from '../../../components/doppe-viewer-action-context/doppe-viewer-action-context';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from "../../../client/doppe-viewer-locales/doppe-viewer-locales";

export const DoppeActionTypeLinksListWidget = React.memo(function DoppeActionTypeLinksListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeLinksList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        renderingInnerStateController,
        observableCurrentItemIdToRender = new ObservableValue<string | null>(null)
    } = useWidgetRenderingContext();

    const actionSettings = actionContext.action.settings;

    const {
        links,
        layout,
        itemClickBehaviour,
        showButton,

        previewShowCoupon,
        previewShowDescription,
        previewShowTitle,
        previewLinkButtonText,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
    } = actionSettings;

    const expandedLinkId = useObservableValue(observableCurrentItemIdToRender);

    const currentLinkToShow = useMemo(
        () => links.find(link => link.id === expandedLinkId) ?? null,
        [links, expandedLinkId]);

    const bymoPageContextProps = useBymoPageContextProps();

    const availableItems = useMemo(() => {
        return iterableMapToArray(links, (listItem) => {
            return {
                ...listItem,
                businessEntityDto: undefined
            }
        })
    }, [links, bymoPageContextProps]);

    const listViewDataSource = useMemo(() => {
        return listViewCreateDataSource(availableItems, {
            idFunction: item => item.id
        });
    }, [availableItems]);

    const rootElementRef = useRef(null);
    const doppeViewerLocales = useDoppeViewerLocales();

    const isRenderedInPage = useIsWidgetRenderedInPage();

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
            ref={rootElementRef}
        >
            {evaluateFunction(() => {
                if (!currentLinkToShow) {
                    if (listViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                layout={layout}
                                listTitle={actionSettings.mainTitle}
                                media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                                hasSeeMoreButton={showButton}
                                onItemClick={eventData => {
                                    onListItemClick({
                                        itemClickBehaviour: !isRenderedInPage  && itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ? DoppeActionListItemClickBehaviour.PreviewItem : itemClickBehaviour,
                                        renderingInnerStateController,
                                        observableCurrentItemIdToRender,
                                        rootElementRef,
                                        actionContext: actionContext as DoppeViewerActionContext<any>,
                                        itemId: eventData.id,
                                        itemIndex: links.findIndex(item => item.id === eventData.id),
                                        itemLink: eventData.url,
                                        couponCode: eventData.couponCode,
                                        viewerLang: doppeViewerLocales,
                                    })
                                }}
                                actionContext={actionContext}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        image,
                                        title,
                                        description,
                                        ribbon,
                                    } = dataItem;

                                    return (
                                        <DoppeActionTypeListViewItem
                                            actionContext={actionContext as DoppeViewerActionContext<any>}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={title}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={image ?? null}
                                            layout={layout}
                                            isOneItem={listViewDataSource.length === 1}
                                            isLink={itemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink}
                                            ribbon={doppeHideableValueIsVisibleAndNotEqualValue(ribbon, '') ? doppeHideableValueGetValue(ribbon) : undefined}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={listViewDataSource}
                            />
                        )
                    }
                } else {
                    return (
                        <DoppeActionTypeLinkWidgetInner
                            reportGoalAchieved={() => {
                                actionContext.reportGoalAchieved(currentLinkToShow.id, links.findIndex(item => item.id === expandedLinkId));
                            }}
                            actionId={actionContext.action.id}
                            actionSettings={{
                                image: currentLinkToShow.image ? asArray(currentLinkToShow.image) : null,
                                couponCode: doppeHideableValueToggleHidden(
                                    currentLinkToShow.couponCode,
                                    isHidden => isHidden ? true : !previewShowCoupon
                                ),
                                description: doppeHideableValueToggleHidden(
                                    currentLinkToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                title: doppeHideableValueToggleHidden(
                                    currentLinkToShow.title,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                linkButtonText: doppeHideableValueGetVisibleValue(
                                    currentLinkToShow.linkButtonText,
                                    previewLinkButtonText
                                ),
                                url: currentLinkToShow.url,
                                ribbon: currentLinkToShow.ribbon,
                            }}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


